import { Maybe } from '@aibees/shared/definition';
import {
  CardActionsProps,
  cardClasses,
  CardContentProps,
  cardHeaderClasses,
  CardHeaderProps,
  CardProps,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { COLORS_SCHEMA } from '../../palette';
import { Padding } from '../../types';

// NEW VARIANT
declare module '@mui/material/Card' {
  interface CardOwnProps {
    headerDivider?: boolean;
  }
}

declare module '@mui/material/CardContent' {
  interface CardContentOwnProps {
    padding?: Padding;
  }
}

declare module '@mui/material/CardHeader' {
  interface CardHeaderOwnProps {
    padding?: Padding;
  }
}

declare module '@mui/material/CardActions' {
  interface CardActionsProps {
    padding?: Padding;
  }
}

function paddingStyles(padding: Maybe<Padding>, theme: Theme) {
  if (padding === 'normal') {
    return theme.spacing(1);
  } else if (padding === 'extra') {
    return theme.spacing(1.5);
  } else if (padding === 'none') {
    return theme.spacing(0);
  } else return theme.spacing(1);
}

export default function Card(theme: Theme) {
  const cardHeaderStyles = (ownerState: CardHeaderProps) => {
    const defaultStyle = {
      [`.${cardHeaderClasses.action}`]: {
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'center',
      },
      padding: paddingStyles(ownerState.padding, theme),
    };
    return defaultStyle;
  };

  const cardContentStyles = (ownerState: CardContentProps) => {
    const defaultStyle = {
      padding: paddingStyles(ownerState.padding, theme),
      // Override Material-UI's default last-child padding-bottom: 24px
      '&:last-child': {
        paddingBottom: paddingStyles(ownerState.padding, theme),
      },
    };
    return defaultStyle;
  };

  const cardActionsStyles = (ownerState: CardActionsProps) => {
    const defaultStyle = {
      padding: paddingStyles(ownerState.padding, theme),
    };
    return defaultStyle;
  };

  const rootStyles = (ownerState: CardProps) => {
    // these are Paper variants overrided in Accordion theme overrides
    const outlinedVariant = ownerState.variant === 'outlined';
    const filledVariant = ownerState.variant === 'filled';
    const elevationVariant = ownerState.variant === 'elevation';

    const defaultStyle = {
      position: 'relative',
      boxShadow: elevationVariant ? theme.customShadows.card : 'none',
      borderRadius: ownerState.square ? 0 : theme.shape.borderRadius * 0.5,
      zIndex: 0, // Fix Safari overflow: hidden with border radius
      [`&.${cardClasses.root} .${cardHeaderClasses.root}`]: {
        fontSize: '16px',
        borderBottom: ownerState.headerDivider
          ? `1px solid ${theme.palette.divider}`
          : 'none',
      },
    };

    const colorStyle = Object.values(COLORS_SCHEMA).map((color) => ({
      ...(ownerState.color === color && {
        backgroundColor: theme.palette.background.paper,

        // FILLED
        ...(filledVariant && {
          backgroundColor:
            color === 'primary'
              ? theme.palette[color][50]
              : theme.palette[color][200],
        }),

        // OUTLINED
        ...(outlinedVariant && {
          border: `solid 1px ${theme.palette[color][300]}`,
        }),
      }),
    }));

    return [defaultStyle, ...colorStyle];
  };
  return {
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: ({ ownerState }: { ownerState: CardProps }) =>
          rootStyles(ownerState),
      },
    },
    MuiCardHeader: {
      defaultProps: {
        padding: 'normal',
        titleTypographyProps: {
          variant: 'body1',
          fontSize: '20px',
          fontWeight: 'bold',
        },
        subheaderTypographyProps: {
          variant: 'body2',
          marginTop: theme.spacing(0.5),
        },
      },
      styleOverrides: {
        root: ({ ownerState }: { ownerState: CardHeaderProps }) =>
          cardHeaderStyles(ownerState),
      },
    },
    MuiCardContent: {
      defaultProps: {
        padding: 'normal',
      },
      styleOverrides: {
        root: ({ ownerState }: { ownerState: CardContentProps }) =>
          cardContentStyles(ownerState),
      },
    },
    MuiCardActions: {
      defaultProps: {
        padding: 'normal',
      },
      styleOverrides: {
        root: ({ ownerState }: { ownerState: CardActionsProps }) =>
          cardActionsStyles(ownerState),
      },
    },
  };
}
